<!-- lib/components/Spinner.svelte -->
<script>
  export let size = "medium"; // small, medium, large
  export let variant = "gold"; // gold, light, dark
  export let label = "Loading...";

  $: sizeClass = `spinner--${size}`;
  $: variantClass = `spinner--${variant}`;
</script>

<div class="spinner-container" role="status" aria-label={label}>
  <div class="spinner {sizeClass} {variantClass}">
    <div class="spinner-ring"></div>
    <div class="spinner-ring"></div>
    <div class="spinner-ring"></div>
  </div>
  <span class="sr-only">{label}</span>
</div>

<style lang="scss">
  .spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }

  .spinner {
    position: relative;
    display: inline-block;
    animation: spin 1.5s linear infinite;
  }

  .spinner-ring {
    position: absolute;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }

  // Size variants
  .spinner--small {
    width: 24px;
    height: 24px;

    .spinner-ring {
      border-width: 2px;

      &:nth-child(1) {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        animation-delay: 0s;
      }

      &:nth-child(2) {
        top: 3px;
        left: 3px;
        width: calc(100% - 6px);
        height: calc(100% - 6px);
        animation-delay: -0.5s;
      }

      &:nth-child(3) {
        top: 6px;
        left: 6px;
        width: calc(100% - 12px);
        height: calc(100% - 12px);
        animation-delay: -1s;
      }
    }
  }

  .spinner--medium {
    width: 40px;
    height: 40px;

    .spinner-ring {
      border-width: 3px;

      &:nth-child(1) {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        animation-delay: 0s;
      }

      &:nth-child(2) {
        top: 5px;
        left: 5px;
        width: calc(100% - 10px);
        height: calc(100% - 10px);
        animation-delay: -0.5s;
      }

      &:nth-child(3) {
        top: 10px;
        left: 10px;
        width: calc(100% - 20px);
        height: calc(100% - 20px);
        animation-delay: -1s;
      }
    }
  }

  .spinner--large {
    width: 56px;
    height: 56px;

    .spinner-ring {
      border-width: 4px;

      &:nth-child(1) {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        animation-delay: 0s;
      }

      &:nth-child(2) {
        top: 7px;
        left: 7px;
        width: calc(100% - 14px);
        height: calc(100% - 14px);
        animation-delay: -0.5s;
      }

      &:nth-child(3) {
        top: 14px;
        left: 14px;
        width: calc(100% - 28px);
        height: calc(100% - 28px);
        animation-delay: -1s;
      }
    }
  }

  // Color variants
  .spinner--gold {
    .spinner-ring {
      border: currentColor solid;
      border-color: var(--text-color1, #d2a552) transparent
        var(--text-color1, #d2a552) transparent;
    }
  }

  .spinner--light {
    .spinner-ring {
      border: currentColor solid;
      border-color: rgba(255, 255, 255, 0.8) transparent
        rgba(255, 255, 255, 0.8) transparent;
    }
  }

  .spinner--dark {
    .spinner-ring {
      border: currentColor solid;
      border-color: rgba(0, 0, 0, 0.6) transparent rgba(0, 0, 0, 0.6)
        transparent;
    }
  }

  // Screen reader only text
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  // Animations
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%,
    40%,
    100% {
      opacity: 1;
    }
    20% {
      opacity: 0.3;
    }
  }

  // Reduce motion for accessibility
  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation: none;
    }

    .spinner-ring {
      animation: pulse 2s ease-in-out infinite;
    }
  }
</style>
